import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import type { CompanySurveyForm } from '@common/models/project/company_survey_form_model'

type TownDisplayInfo = {
  schoolName: string
  schoolPrincipal: string
  schoolPostalCode: string
  schoolAddress: string
  schoolPhone: string
  schoolEmail: string
  projectNumber: string
  projectName: string
  projectStartDate: string
  projectEndDate: string
  projectDescription: string
  projectStartTime: string
  projectEndTime: string
  projectMinExperienceDays: number
  projectMinExperienceTime: number
  projectStudentGrade: string
  participantCount: number
  projectMinParticipantsPerCompany: number
  projectMaxParticipantsPerCompany: number
  projectContactPersonName: string
  projectLunchNotes: string
  /** 事前連絡・訪問の実施予定（will_do 等のコード） */
  projectPriorContactVisitPlan: string
  projectPriorContactVisitSchedule: string
}

type TownContextValue = {
  displayInfo: TownDisplayInfo | null
  setDisplayInfoFromForm: (form: CompanySurveyForm | null | undefined) => void
  clearDisplayInfo: () => void
}

const TownContext = createContext<TownContextValue | undefined>(undefined)

const EMPTY_DISPLAY_INFO: TownDisplayInfo = {
  schoolName: '',
  schoolPrincipal: '',
  schoolPostalCode: '',
  schoolAddress: '',
  schoolPhone: '',
  schoolEmail: '',
  projectNumber: '',
  projectName: '',
  projectStartDate: '',
  projectEndDate: '',
  projectDescription: '',
  projectStartTime: '',
  projectEndTime: '',
  projectMinExperienceDays: 0,
  projectMinExperienceTime: 0,
  projectStudentGrade: '',
  participantCount: 0,
  projectMinParticipantsPerCompany: 0,
  projectMaxParticipantsPerCompany: 0,
  projectContactPersonName: '',
  projectLunchNotes: '',
  projectPriorContactVisitPlan: '',
  projectPriorContactVisitSchedule: '',
}

const toPartialDisplayInfo = (form: CompanySurveyForm): Partial<TownDisplayInfo> => ({
  schoolName: form.schoolName ?? undefined,
  schoolPrincipal: form.schoolPrincipal ?? undefined,
  schoolPostalCode: form.schoolPostalCode ?? undefined,
  schoolAddress: form.schoolAddress ?? undefined,
  schoolPhone: form.schoolPhone ?? undefined,
  schoolEmail: form.schoolEmail ?? undefined,
  projectNumber: form.projectNumber ?? undefined,
  projectName: form.projectName ?? undefined,
  projectStartDate: form.projectStartDate ?? undefined,
  projectEndDate: form.projectEndDate ?? undefined,
  projectDescription: form.projectDescription ?? form.projectName ?? undefined,
  projectStartTime: form.projectStartTime ?? undefined,
  projectEndTime: form.projectEndTime ?? undefined,
  projectMinExperienceDays: form.projectMinExperienceDays ?? undefined,
  projectMinExperienceTime: form.projectMinExperienceTime ?? undefined,
  projectStudentGrade: form.projectStudentGrade ?? undefined,
  participantCount: form.participantCount ?? undefined,
  projectMinParticipantsPerCompany: form.projectMinParticipantsPerCompany ?? undefined,
  projectMaxParticipantsPerCompany: form.projectMaxParticipantsPerCompany ?? undefined,
  projectContactPersonName: form.projectContactPersonName ?? undefined,
  projectLunchNotes: form.projectLunchNotes ?? undefined,
  projectPriorContactVisitPlan: form.projectPriorContactVisitPlan ?? undefined,
  projectPriorContactVisitSchedule: form.projectPriorContactVisitSchedule ?? undefined,
})

const isSameDisplayInfo = (a: TownDisplayInfo | null, b: TownDisplayInfo) => {
  if (!a) return false
  return (
    a.schoolName === b.schoolName &&
    a.schoolPrincipal === b.schoolPrincipal &&
    a.schoolPostalCode === b.schoolPostalCode &&
    a.schoolAddress === b.schoolAddress &&
    a.schoolPhone === b.schoolPhone &&
    a.schoolEmail === b.schoolEmail &&
    a.projectNumber === b.projectNumber &&
    a.projectName === b.projectName &&
    a.projectStartDate === b.projectStartDate &&
    a.projectEndDate === b.projectEndDate &&
    a.projectDescription === b.projectDescription &&
    a.projectStartTime === b.projectStartTime &&
    a.projectEndTime === b.projectEndTime &&
    a.projectMinExperienceDays === b.projectMinExperienceDays &&
    a.projectMinExperienceTime === b.projectMinExperienceTime &&
    a.projectStudentGrade === b.projectStudentGrade &&
    a.participantCount === b.participantCount &&
    a.projectMinParticipantsPerCompany === b.projectMinParticipantsPerCompany &&
    a.projectMaxParticipantsPerCompany === b.projectMaxParticipantsPerCompany &&
    a.projectContactPersonName === b.projectContactPersonName &&
    a.projectLunchNotes === b.projectLunchNotes &&
    a.projectPriorContactVisitPlan === b.projectPriorContactVisitPlan &&
    a.projectPriorContactVisitSchedule === b.projectPriorContactVisitSchedule
  )
}

const mergeDisplayInfo = (prev: TownDisplayInfo | null, partial: Partial<TownDisplayInfo>): TownDisplayInfo => {
  const base = prev ?? EMPTY_DISPLAY_INFO
  return {
    schoolName: partial.schoolName ?? base.schoolName,
    schoolPrincipal: partial.schoolPrincipal ?? base.schoolPrincipal,
    schoolPostalCode: partial.schoolPostalCode ?? base.schoolPostalCode,
    schoolAddress: partial.schoolAddress ?? base.schoolAddress,
    schoolPhone: partial.schoolPhone ?? base.schoolPhone,
    schoolEmail: partial.schoolEmail ?? base.schoolEmail,
    projectNumber: partial.projectNumber ?? base.projectNumber,
    projectName: partial.projectName ?? base.projectName,
    projectStartDate: partial.projectStartDate ?? base.projectStartDate,
    projectEndDate: partial.projectEndDate ?? base.projectEndDate,
    projectDescription: partial.projectDescription ?? base.projectDescription,
    projectStartTime: partial.projectStartTime ?? base.projectStartTime,
    projectEndTime: partial.projectEndTime ?? base.projectEndTime,
    projectMinExperienceDays: partial.projectMinExperienceDays ?? base.projectMinExperienceDays,
    projectMinExperienceTime: partial.projectMinExperienceTime ?? base.projectMinExperienceTime,
    projectStudentGrade: partial.projectStudentGrade ?? base.projectStudentGrade,
    participantCount: partial.participantCount ?? base.participantCount,
    projectMinParticipantsPerCompany:
      partial.projectMinParticipantsPerCompany ?? base.projectMinParticipantsPerCompany,
    projectMaxParticipantsPerCompany:
      partial.projectMaxParticipantsPerCompany ?? base.projectMaxParticipantsPerCompany,
    projectContactPersonName: partial.projectContactPersonName ?? base.projectContactPersonName,
    projectLunchNotes: partial.projectLunchNotes ?? base.projectLunchNotes,
    projectPriorContactVisitPlan:
      partial.projectPriorContactVisitPlan ?? base.projectPriorContactVisitPlan,
    projectPriorContactVisitSchedule:
      partial.projectPriorContactVisitSchedule ?? base.projectPriorContactVisitSchedule,
  }
}

export function TownProvider({ children }: { children: ReactNode }) {
  const [displayInfo, setDisplayInfo] = useState<TownDisplayInfo | null>(null)

  const setDisplayInfoFromForm = useCallback((form: CompanySurveyForm | null | undefined) => {
    if (!form) return
    const partial = toPartialDisplayInfo(form)
    setDisplayInfo((prev) => {
      const next = mergeDisplayInfo(prev, partial)
      return isSameDisplayInfo(prev, next) ? prev : next
    })
  }, [])

  const clearDisplayInfo = useCallback(() => {
    setDisplayInfo(null)
  }, [])

  const value = useMemo<TownContextValue>(
    () => ({
      displayInfo,
      setDisplayInfoFromForm,
      clearDisplayInfo,
    }),
    [displayInfo, setDisplayInfoFromForm, clearDisplayInfo]
  )

  return <TownContext.Provider value={value}>{children}</TownContext.Provider>
}

export function useTownContext() {
  const context = useContext(TownContext)
  if (!context) {
    throw new Error('useTownContext must be used within TownProvider')
  }
  return context
}
